import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import AsyncSelect from 'react-select/async';
import type { SingleValue, StylesConfig } from 'react-select';
import { X } from 'lucide-react';
import { Dialog, DialogContent } from '@components/ui/Dialog';
import { useCardSearch } from './useCardSearch';
import { apiService } from '@services/api';
import { conversationApi } from '@services/conversationApi';
import ConversationCardAddReply from '@components/ConversationItem/ConversationCardAddReply';
import type { ConversationDetail } from '@/types/conversation';
import { config as envConfig } from '@config/env';
import { authStorage } from '@/lib/storage';
import { conversationBus } from '@/lib/conversationBus';
import { useToast } from '@components/ui/Toast';
import { socketService } from '@/services/socketService';
import './SaveAnnotatedImageModal.css';

interface Card {
  value: number;
  label: string;
  projectName?: string;
  conversationTitle?: string;
  phases?: PhaseOption[];
}

interface PhaseOption {
  ConversationPhaseId: number;
  Title: string;
  Name?: string;
}

interface Layer {
  value: number;
  label: string;
  color?: string;
}

interface SaveAnnotatedImageModalProps {
  open: boolean;
  imageBase64: string;
  annotatedImageBase64?: string;
  defaultCardId?: number;
  defaultCardTitle?: string;
  defaultProjectName?: string;
  onClose: () => void;
  onBack: () => void;
  onReplyCreated?: () => void;
  onCreateReply: (
    cardId: number,
    layerId: number,
    layerName: string,
    layerColor: string,
    text: string,
    additionalImages?: File[],
    phaseId?: number,
    phaseName?: string
  ) => void;
}

function debounce<T extends(...args: any[]) => any>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void
{
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) =>
  {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

interface LayerResponse {
  LayerId: number;
  Name: string;
  Color?: string;
}

const selectStyles: StylesConfig<any, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: '32px',
    borderRadius: '4px',
    boxShadow: 'none',
    fontSize: '13px',
    backgroundColor: '#fff',
    borderColor: state.isFocused ? '#62accb' : '#ccc',
    '&:hover': {
      borderColor: '#62accb',
    },
  }),
  menu: base => ({
    ...base,
    borderRadius: '4px',
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '13px',
    backgroundColor: state.isFocused ? '#62accb' : '#fff',
    color: state.isFocused ? '#fff' : '#333',
    cursor: 'pointer',
  }),
};

export const SaveAnnotatedImageModal: React.FC<SaveAnnotatedImageModalProps> = ({
  open,
  imageBase64,
  defaultCardId,
  defaultCardTitle,
  defaultProjectName,
  annotatedImageBase64,
  onClose,
  onBack,
  onReplyCreated,
  onCreateReply,
}) =>
{
  const { loadCards } = useCardSearch();
  const toast = useToast();

  void onCreateReply;

  const defaultCard = useMemo<Card | null>(() =>
  {
    if (defaultCardId && defaultCardTitle)
    {
      return {
        value: defaultCardId,
        label: `(${defaultProjectName || 'No project'}) ${defaultCardTitle}`,
        projectName: defaultProjectName,
        conversationTitle: defaultCardTitle,
      };
    }
    return null;
  }, [defaultCardId, defaultCardTitle, defaultProjectName]);

  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [selectedPhase, _setSelectedPhase] = useState<PhaseOption | null>(null);
  const [_availableLayers, setAvailableLayers] = useState<Layer[]>([]);
  const [selectedLayer, setSelectedLayer] = useState<Layer | null>(null);
  const [_loadingLayers, setLoadingLayers] = useState(false);
  const [replyText, _setReplyText] = useState('');
  const [additionalImages, _setAdditionalImages] = useState<File[]>([]);
  const [_isDragging, _setIsDragging] = useState(false);

  void selectedPhase;
  void replyText;
  void additionalImages;
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [conversation, setConversation] = useState<ConversationDetail | null>(null);
  const replyComponentRef = useRef<any>(null);
  const [initialCardOptions, setInitialCardOptions] = useState<Card[]>([]);
  const [isCreatingReply, setIsCreatingReply] = useState(false);
  const [hasUserCleared, setHasUserCleared] = useState(false);
  const [lastSearchResults, setLastSearchResults] = useState<Card[]>([]);
  const [replyComponentKey, setReplyComponentKey] = useState(0);

  const handleCardChange = useCallback((card: SingleValue<Card>) =>
  {
    setSelectedCard(card);
    setHasUserCleared(card === null);
  }, []);

  const loadCardOptions = useCallback(
    (inputValue: string, callback: (options: Card[]) => void) =>
    {
      if (inputValue.length < 2)
      {
        callback(initialCardOptions.length > 0 ? initialCardOptions : lastSearchResults);
        return;
      }

      loadCards(inputValue)
        .then(cards =>
        {
          setLastSearchResults(cards);
          callback(cards);
        })
        .catch(() => callback(lastSearchResults));
    },
    [loadCards, lastSearchResults, initialCardOptions],
  );

  const debouncedLoadCardOptions = useMemo(
    () => debounce(loadCardOptions, 1000),
    [loadCardOptions],
  );

  useEffect(() =>
  {
    if (!open)
    {
      setConversation(null);
      return;
    }

    if (!selectedCard?.value)
    {
      setConversation(null);
      return;
    }

    const loadConversation = async () =>
    {
      setLoadingConversation(true);

      try
      {
        const token = authStorage.getAccessToken();

        if (!token)
        {
          throw new Error('No authentication token found');
        }

        const response = await conversationApi.getConversationById(
          envConfig.api.baseUrl,
          token,
          selectedCard.value,
          {
            getAllReplies: false,
            isConversationDetailsView: true,
          },
        );

        if (!response || !response.ConversationId)
        {
          throw new Error(`Invalid conversation response`);
        }

        setConversation(response as ConversationDetail);
      }
      catch (error)
      {
        console.error('[SaveAnnotatedImageModal] Error loading conversation:', error);
        setConversation(null);
      }
      finally
      {
        setLoadingConversation(false);
      }
    };

    loadConversation();
  }, [selectedCard, open, defaultCardId]);

  useEffect(() =>
  {
    if (!conversation) return;

    let attempts = 0;
    const maxAttempts = 10;

    const waitForRefAndExpand = () =>
    {
      attempts++;

      if (replyComponentRef.current?.onEnableEditor)
      {
        const fakeEvent = { preventDefault: () => {} } as React.MouseEvent;
        replyComponentRef.current.onEnableEditor(fakeEvent);

        if (annotatedImageBase64 || imageBase64)
        {
          const imageToInsert = annotatedImageBase64 || imageBase64;
          let summernoteAttempts = 0;
          const maxSummernoteAttempts = 20;

          const waitForSummernoteAndInsert = () =>
          {
            summernoteAttempts++;

            if ((window as any).$)
            {
              const $ = (window as any).$;
              const $container = $('.corgee_com_card-add-replay-container');
              const $noteEditable = $container.find('.note-editable');
              const $summernoteElement = $noteEditable.closest('.note-editor').prev();

              if ($summernoteElement.length && $summernoteElement.data('summernote'))
              {
                try
                {
                  if ($noteEditable.length)
                  {
                    $noteEditable.focus();
                    $noteEditable.trigger('click');
                  }

                  setTimeout(() =>
                  {
                    $summernoteElement.summernote('insertImage', imageToInsert);

                    setTimeout(() =>
                    {
                      const editable = $summernoteElement.next('.note-editor').find('.note-editable')[0];
                      if (editable)
                      {
                        const newParagraph = document.createElement('p');
                        newParagraph.style.fontFamily = 'Roboto, sans-serif';
                        newParagraph.innerHTML = '<br>';
                        editable.appendChild(newParagraph);

                        const range = document.createRange();
                        const sel = window.getSelection();
                        range.setStart(newParagraph, 0);
                        range.collapse(true);
                        sel?.removeAllRanges();
                        sel?.addRange(range);
                      }
                    }, 50);
                  }, 100);
                }
                catch (error)
                {
                  console.error('[SaveAnnotatedImageModal] Error inserting image:', error);
                }
                return;
              }
            }

            if (summernoteAttempts < maxSummernoteAttempts)
            {
              setTimeout(waitForSummernoteAndInsert, 300);
            }
            else
            {
              console.warn('[SaveAnnotatedImageModal] Summernote editor not found after', maxSummernoteAttempts, 'attempts');
            }
          };

          setTimeout(waitForSummernoteAndInsert, 500);
        }

        return;
      }

      if (attempts < maxAttempts)
      {
        setTimeout(waitForRefAndExpand, 200);
      }
      else
      {
        console.warn('[SaveAnnotatedImageModal] Reply component ref not available after', maxAttempts, 'attempts');
      }
    };

    setTimeout(waitForRefAndExpand, 100);

  }, [conversation, annotatedImageBase64, imageBase64]);

  useEffect(() =>
  {
    if (open && defaultCard && !selectedCard && !hasUserCleared)
    {
      setSelectedCard(defaultCard);
    }
  }, [open, defaultCard, selectedCard, hasUserCleared]);

  useEffect(() =>
  {
    if (!open)
    {
      setHasUserCleared(false);
    }
  }, [open]);

  const handleAddLayerToCard = useCallback(
    async (
      _e: React.MouseEvent,
      layer: any,
      _functions?: { onUpdateLayerToCard?: (layerId: number) => void },
    ) =>
    {
      if (!conversation || !selectedCard?.value) return;

      try
      {
        const layerToAdd = {
          LayerId: layer.LayerId,
          Name: layer.Name || layer.LayerName,
          Color: layer.Color,
        };

        const updatedConversation = {
          ...conversation,
          ConversationLayers: [
            ...(conversation.ConversationLayers || []),
            layerToAdd,
          ],
        };

        await apiService.put('conversation/UpdateLayers', { ...updatedConversation, SocketConnectionId: socketService.connectionId });

        const token = authStorage.getAccessToken();
        if (!token) return;

        const reloadedConversation = await conversationApi.getConversationById(
          envConfig.api.baseUrl,
          token,
          selectedCard.value,
          {
            getAllReplies: false,
            isConversationDetailsView: true,
          },
        );

        if (reloadedConversation)
        {
          setConversation(reloadedConversation as ConversationDetail);
          setReplyComponentKey(prev => prev + 1);
        }
      }
      catch (error)
      {
        console.error('[SaveAnnotatedImageModal] Error adding layer to card:', error);
        toast.error('Error', 'Failed to add layer to card');
      }
    },
    [conversation, selectedCard, toast],
  );

  useEffect(() =>
  {
    if (!open) return;

    if (defaultCard)
    {
      setInitialCardOptions([defaultCard]);
      return;
    }

    const loadInitialCards = async () =>
    {
      try
      {
        const cards = await loadCards('');
        setInitialCardOptions(cards.slice(0, 20));
      }
      catch (error)
      {
        console.error('[SaveAnnotatedImageModal] Error loading initial cards:', error);
      }
    };

    loadInitialCards();
  }, [open, loadCards, defaultCard]);

  useEffect(() =>
  {
    if (!open) return;

    const loadLayers = async () =>
    {
      setLoadingLayers(true);
      try
      {
        const response = await apiService.get<LayerResponse[]>('layer/getpersonlayers');
        const layers: Layer[] = response.map(layer => ({
          value: layer.LayerId,
          label: layer.Name,
          color: layer.Color || '#62accb',
        }));
        setAvailableLayers(layers);

        if (!selectedLayer && layers.length > 0)
        {
          const defaultLayerId = localStorage.getItem('C2_layerid');
          const defaultLayer = defaultLayerId
            ? layers.find(l => l.value === Number(defaultLayerId))
            : null;
          setSelectedLayer(defaultLayer || layers[0]!);
        }
      }
      catch (error)
      {
        console.error('[SaveAnnotatedImageModal] Error loading layers:', error);
        setAvailableLayers([]);
      }
      finally
      {
        setLoadingLayers(false);
      }
    };

    loadLayers();
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && !isCreatingReply && onClose()}>
      <DialogContent className="save-modal p-0 max-w-[800px] max-h-[90vh] overflow-hidden flex flex-col">
        {isCreatingReply && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              borderRadius: '8px',
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #62accb',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
            <p style={{ marginTop: '16px', color: '#333', fontSize: '14px', fontWeight: 500 }}>
              Creating reply...
            </p>
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
          </div>
        )}

        <div className="save-modal-header">
          <h3>Create Reply</h3>
          <button className="save-modal-close" onClick={onClose} title="Close" disabled={isCreatingReply}>
            <X size={20} />
          </button>
        </div>

        <div className="save-modal-body overflow-y-auto flex-1">
          <div className="save-modal-task-field">
            <label>Item:</label>
            <AsyncSelect
              value={selectedCard}
              onChange={handleCardChange}
              loadOptions={debouncedLoadCardOptions}
              defaultOptions={initialCardOptions.length > 0 ? initialCardOptions : false}
              cacheOptions
              styles={selectStyles}
              placeholder="Search or select a task..."
              loadingMessage={() => 'Searching...'}
              noOptionsMessage={({ inputValue }) =>
                inputValue.length < 2
                  ? 'Type at least 2 characters to search...'
                  : 'No tasks found'
              }
              isClearable
            />
          </div>

          {loadingConversation && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                Loading conversation...
            </div>
          )}

          {!loadingConversation && conversation && (
            <div className="save-modal-field" style={{ marginBottom: 0 }}>
              <ConversationCardAddReply
                key={replyComponentKey}
                ref={replyComponentRef}
                conversation={conversation as any}
                api={{
                  url: envConfig.api.baseUrl,
                  token: authStorage.getAccessToken() || '',
                }}
                config={{ weburl: envConfig.web.url }}
                userData={{
                  personName: localStorage.getItem('C2_user_name') || 'User',
                  imageProfile: localStorage.getItem('C2_user_image') || 'localhost/Corgee.WebClient/Images/user.png',
                }}
                onAddLayerToCard={handleAddLayerToCard}
                onAddReply={(conversationId, reply, isUpdate, isNew) =>
                {
                  if (isNew && !isUpdate)
                  {
                    setIsCreatingReply(true);
                  }

                  if (isUpdate && !isNew)
                  {
                    setIsCreatingReply(false);
                    toast.success('Your dashboard has changed', 'Reply created successfully');
                    conversationBus.emit('reply-added', { conversationId, reply });
                    onClose();

                    if (onReplyCreated)
                    {
                      onReplyCreated();
                    }
                  }
                }}
              />
            </div>
          )}

          {!loadingConversation && !conversation && selectedCard && (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <div style={{ color: '#e74c3c', marginBottom: '10px' }}>
                  ⚠️ Failed to load conversation
              </div>
              <div style={{ color: '#999', fontSize: '13px' }}>
                  Conversation ID: {selectedCard.value}
                <br />
                  Please try selecting another task or check console for details.
              </div>
            </div>
          )}
        </div>

        <div className="save-modal-footer">
          <button
            className="save-modal-btn save-modal-btn-cancel"
            onClick={onBack}
            title="Back to editor"
            disabled={isCreatingReply}
          >
            ← Back
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
